import { Badge, Form, Input, Tabs } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useTranslation } from "react-i18next";
import {
    defaultLocale,
    localizationEnabled,
    SupportedLocale,
    supportedLocales
} from "../config/locales";
import { contentLocaleLabel } from "../i18n/contentLocaleLabel";
import {
    getLocalizedValue,
    isLocaleComplete,
    LocalizedEntity,
    setLocalizedValue,
    TranslatableField
} from "../utils/translations";

interface LocalizedFieldsProps<K extends string, T extends LocalizedEntity<K>> {
    fields: ReadonlyArray<TranslatableField<K>>;
    value: T;
    onChange: (next: T) => void;
}

type Completeness = "complete" | "partial" | "empty";

function completeness<K extends string>(
    value: LocalizedEntity<K>,
    fields: ReadonlyArray<TranslatableField<K>>,
    locale: string
): Completeness {
    const keys = fields.map(field => field.key);
    if (isLocaleComplete(value, keys, locale)) return "complete";
    const anyFilled = keys.some(key => getLocalizedValue(value, key, locale).trim().length > 0);
    return anyFilled ? "partial" : "empty";
}

const badgeStatus: Record<Completeness, "success" | "warning" | "default"> = {
    complete: "success",
    partial: "warning",
    empty: "default"
};

/**
 * Edits every translatable field of an entity, one tab per configured locale.
 *
 * The default locale's tab writes to the entity's own fields (`name`,
 * `description`, ...) — that is where the API keeps the fallback value. Every
 * other tab writes to the entity's `translations` array. Callers therefore get
 * an entity back that is ready to send, and never have to know which storage a
 * given locale uses.
 *
 * Locales come from `REACT_APP_SUPPORTED_LOCALES`; see `config/locales.ts`.
 */
export function LocalizedFields<K extends string, T extends LocalizedEntity<K>>({
    fields,
    value,
    onChange
}: LocalizedFieldsProps<K, T>) {
    const { t, i18n } = useTranslation();

    const change = (field: K, locale: string, next: string) => {
        onChange(setLocalizedValue(value, field, locale, next));
    };

    const fieldInputs = (locale: SupportedLocale) =>
        fields.map(field => (
            <Form.Item key={field.key} label={`${t(field.labelKey)}:`}>
                {field.type === "textarea" ? (
                    <TextArea
                        rows={field.rows ?? 8}
                        placeholder={field.placeholderKey != null ? t(field.placeholderKey) : ""}
                        value={getLocalizedValue(value, field.key, locale.code)}
                        onChange={e => change(field.key, locale.code, e.target.value)}
                    />
                ) : (
                    <Input
                        placeholder={field.placeholderKey != null ? t(field.placeholderKey) : ""}
                        value={getLocalizedValue(value, field.key, locale.code)}
                        onChange={e => change(field.key, locale.code, e.target.value)}
                    />
                )}
            </Form.Item>
        ));

    // Nothing to switch between when this deploy target has one locale — render
    // the fields the way the pages did before localization existed.
    if (!localizationEnabled) {
        return <div>{fieldInputs(defaultLocale)}</div>;
    }

    const tabTitle = (locale: SupportedLocale) => (
        <span>
            <Badge status={badgeStatus[completeness(value, fields, locale.code)]} />
            {contentLocaleLabel(locale, i18n.language)}
            {locale.code === defaultLocale.code ? ` ${t("localizedFields.defaultLocaleSuffix")}` : ""}
        </span>
    );

    return (
        <Tabs size="small" type="card">
            {supportedLocales.map(locale => (
                <Tabs.TabPane tab={tabTitle(locale)} key={locale.code}>
                    {fieldInputs(locale)}
                </Tabs.TabPane>
            ))}
        </Tabs>
    );
}
